<template>
    <div class="session-property-rule">
        <template v-if="!isLocaleEnglish">
            <div class="flex-break" />
            <strong>{{ $t('Criteria') }}</strong>
            <div class="flex-break" />
        </template>
        <simple-select
            :options="operatorOptions"
            :field="['operator']"
            :check-valid-rule="checkValidRule"
            :value="operatorValue"
            :disabled="readOnly"
            preselect-first
            @input="$emit('input', $event)" />
        <pendo-multiselect
            v-if="operatorNotEmpty"
            :placeholder="$t('Select a value')"
            searchable
            taggable
            min-trigger-width="400"
            :value="resourceValue"
            :options="resources"
            @input="onInput"
            @tag="onInput">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </pendo-multiselect>
        <template v-if="resourceValue">
            <div
                v-if="!isLocaleEnglish"
                class="flex-break" />
            <simple-select
                :options="timeOptions"
                :value="timeValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="time"
                @input="$emit('input', $event)" />
            <component
                :is="dateComponent.component"
                v-if="dateComponent"
                v-bind="dateComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <div
                v-if="showDateWarning"
                class="date-warning">
                <pendo-icon
                    v-pendo-tooltip="{
                        arrow: true,
                        multiline: true,
                        content: $t(
                            'sessionPropertyRule.Session properties are not available prior to May 1, 2025. Please update your date range.',
                        ),
                        placement: 'auto',
                    }"
                    type="alert-triangle"
                    size="20" />
            </div>
        </template>
    </div>
</template>

<script>
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';

import {
    getTimeOptions,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getOperatorOptions,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { PendoMultiselect, PendoIcon, PendoTooltip } from '@pendo/components';
import moment from '@utils/moment-wrapper';

const UTM_DATE = 1746057600000;

export default {
    name: 'SessionPropertyRule',
    components: {
        SimpleSelect,
        PendoMultiselect,
        AbsoluteDateRange,
        RelativeDateRange,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            timeOptions: [],
        };
    },
    computed: {
        operatorNotEmpty () {
            const opValue = this.operatorValue?.operator || '';

            return opValue !== '!empty' && opValue !== 'empty';
        },
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        resourceValue () {
            return this.rule?.value;
        },
        operatorOptions () {
            if (this.rule.schema !== undefined || this.rule.id !== undefined) {
                return getOperatorOptions({ schema: 'string', id: this.rule.id });
            }

            return undefined;
        },
        operatorValue () {
            return this.operatorOptions?.find((option) => option.operator === this.rule.operator);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        dateComponent () {
            return {
                withinLast: getRelativeDateRangeConfig(this.rule),
                between: getAbsoluteDateRangeConfig(this.rule),
            }[this.rule.time];
        },
        showDateWarning () {
            if (
                this.rule.time === 'between' &&
                moment(this.rule.first, { ignoreTimeZone: true }).valueOf() < UTM_DATE
            ) {
                return true;
            }

            if (this.rule.time === 'withinLast') {
                const today = moment({ ignoreTimeZone: true }).startOf('day');
                const shifted = today.clone().subtract(this.rule.count, this.rule.granularity);
                const shiftedMs = shifted.valueOf();

                if (shiftedMs < UTM_DATE) {
                    return true;
                }
            }

            return false;
        },
    },
    created () {
        this.timeOptions = getTimeOptions().aggregation;
    },
    methods: {
        onInput ($event) {
            this.$emit('input', { field: 'value', value: $event });
        },
    },
};
</script>
<style lang="less" scoped>
.date-warning {
    margin-top: 8px;
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
